import { useMemo } from "react";

import styled from "styled-components";

import { colorsPalettes } from "@similarweb/styles";
import { IconButton } from "@similarweb/ui-components";

import { swSettings } from "app/common/services/swSettings";
import { EErrorTypes } from "app/components/React/empty-state/GenericEmptyState";
import { WidgetContainer } from "app/components/React/WidgetContainer/WidgetContainer";
import { useSubtitleFilters } from "app/custom-hooks/useSubtitleFilters";
import { useWindowSize } from "app/custom-hooks/useWIndowSize";
import { translate } from "app/filters/i18nFilter";
import { openHookModal } from "app/services/ModalService";
import { allTrackers } from "app/services/track/track";
import { isNoTouch } from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";
import { displayAdvertisingNavigation } from "single-spa/react-app/config/solutions25/adIntelligence/displayAdvertising/displayAdvertisingNavigator";

import { useAdvertiserActivityDataAndParams } from "../hooks/useAdvertiserActivityDataAndParams";
import { ContentBlur } from "../shared/ContentBlur";
import { AD_INTELLIGENCE_HOOKS } from "../utils/adIntelligenceHooks";
import { AdIntelligenceHookButton, HookOverlayContent } from "../shared/TableComponents";
import {
    getNoDataObject,
    hasAdIntelBetaAccess,
    hasAdIntelligenceOverviewAccess,
    isAdIntelligenceNoTouchPreviewVariant,
    openAdIntelligenceHookModal,
} from "../utils/utils";

import { CountryMapAndTable } from "./CountryMapAndTable";
import { getMapDimensionsByWindowWidth } from "./configs/CountryMapWidgetConfigs";
import { useCountries } from "./hooks/useCountries";
import { TableFooter } from "./StyledComponents";

const ELEMENT_ID = "countries-map";

const TableFooterWithBorderTop = styled(TableFooter)`
    border-top: 1px solid ${colorsPalettes.carbon[100]};
`;

interface ICountryMapWidgetProps {
    isPreview?: boolean;
    titleKey?: string;
    tooltipKey?: string;
    methodName?: "adIntelligenceAdvertiserWorldMapDataList" | "adIntelligenceAdvertiserWorldMapMergedDataList";
}

export const CountryMapWidget = ({ isPreview = false, titleKey, tooltipKey, methodName }: ICountryMapWidgetProps) => {
    const params = useAdvertiserActivityDataAndParams();
    const { data, isLoading, isError } = useCountries(methodName);
    const noDataObject = getNoDataObject(isError);
    const { width } = useWindowSize();
    const mapDimensions = useMemo(() => getMapDimensionsByWindowWidth(width), [width]);
    const subtitleFilters = useSubtitleFilters("date", "webSource");
    const hasAdIntelBeta = hasAdIntelligenceOverviewAccess(isPreview);
    const isNoTouchVariant = isAdIntelligenceNoTouchPreviewVariant(isPreview);
    const isNoTouchUser = isNoTouch(swSettings);
    const hasBetaAccess = hasAdIntelBetaAccess();

    // NoTouch user that already has beta access — should be upsold to a touch plan via the hook modal.
    const shouldOpenUpgradeToTouchHook = isNoTouchUser && hasBetaAccess;
    // Preview-mode NoTouch user without beta access — content must be blurred behind the hook overlay.
    const shouldBlurPreviewContent = isPreview && isNoTouchUser && !hasBetaAccess;
    // Footer is rendered only for limited views (preview / NoTouch), and never when the content is blurred behind the hook overlay.
    const shouldShowFooter = (isPreview || isNoTouchUser) && !shouldBlurPreviewContent;
    // Touch user with beta access sees the regular "see all" navigation button instead of the hook CTA.
    const shouldShowSeeAllButton = hasAdIntelBeta && !isNoTouchUser;

    // Preview and NoTouch users only see a truncated list — pagination is disabled in this mode.
    const shouldLimitData = isPreview || isNoTouchUser;

    const hasData = (data?.countrySpendShares?.length ?? 0) > 0;
    const shouldShowNoData = !isLoading && !hasData;

    const openUpgradeToTouchHookModal = () => {
        openHookModal(AD_INTELLIGENCE_HOOKS.advertiser.upgradeToTouch);
    };

    const handleSeeAll = () => {
        if (shouldOpenUpgradeToTouchHook) {
            openUpgradeToTouchHookModal();
        } else if (hasAdIntelBeta) {
            allTrackers.dispatchTrackEvent("Internal Link", "click", "Advertiser_overview/Countries_map/see_more");
            void displayAdvertisingNavigation.overview.go({
                ...params,
                // @ts-expect-error - send id to the state
                // eslint-disable-next-line @typescript-eslint/naming-convention
                "#": ELEMENT_ID,
            });
        } else {
            openAdIntelligenceHookModal();
        }
    };

    return (
        <div id={ELEMENT_ID}>
            <WidgetContainer
                className="col-xs"
                title={translate(
                    titleKey ?? `ad.intelligence.overview.countries.map.title.${isPreview ? "preview" : "full"}`,
                )}
                tooltip={translate(
                    tooltipKey ?? `ad.intelligence.overview.countries.map.tooltip.${isPreview ? "preview" : "full"}`,
                )}
                subtitleFilters={subtitleFilters}
                isLoading={isLoading}
                showSubtitleFiltersLoadingState={false}
                loaderHeight={mapDimensions.height}
                loaderWidth="100%"
                bodyMinHeight={mapDimensions.height}
                withBorder
                noData={shouldShowNoData}
                noDataTitle={noDataObject.title}
                noDataSubtitle={noDataObject.subtitle}
                noDataType={isError ? EErrorTypes.ERROR : EErrorTypes.NO_DATA}
            >
                <ContentBlur
                    enabled={shouldBlurPreviewContent}
                    content={
                        <HookOverlayContent
                            title={translate("ad.intelligence.overview.countries.map.preview.title")}
                            subtitle={translate("ad.intelligence.overview.countries.map.preview.subtitle")}
                            buttonText={translate("ad.intelligence.overview.countries.map.preview.button")}
                            isNoTouchVariant={isNoTouchVariant}
                            onButtonClick={() => openAdIntelligenceHookModal()}
                            dataAutomation="hook-overlay-country-map"
                        />
                    }
                >
                    <CountryMapAndTable
                        data={data}
                        mapDimensions={mapDimensions}
                        shouldLimitData={shouldLimitData}
                        resetKey={`${params.duration}-${params.country}`}
                    />
                </ContentBlur>
                {shouldShowFooter && hasData ? (
                    <TableFooterWithBorderTop>
                        {shouldShowSeeAllButton ? (
                            <IconButton
                                type="flat"
                                onClick={handleSeeAll}
                                iconName="arrow-right"
                                placement="right"
                                iconSize="xs"
                            >
                                {translate("ad.intelligence.overview.countries.map.see.all.button")}
                            </IconButton>
                        ) : (
                            <AdIntelligenceHookButton
                                dataAutomation="ad-intelligence-country-map-hook-button"
                                buttonText={translate("ad.intelligence.overview.countries.map.preview.button")}
                                isNoTouchVariant={isNoTouchVariant}
                                onButtonClick={handleSeeAll}
                            />
                        )}
                    </TableFooterWithBorderTop>
                ) : null}
            </WidgetContainer>
        </div>
    );
};
